<template>
  <button
    class="flex gap-2 items-center"
    :disabled="loading.isLoading.value"
    @click.stop="togglePlanLock()"
  >
    <div
      class="inline-flex rounded-full relative transition-colors duration-200 ease-in-out border-2 border-transparent focus:ring-2 focus:ring-green-600 focus:ring-offset-2 w-11 h-6"
      :class="[iconState ? 'bg-red-600' : 'bg-green-500']"
    >
      <div
        class="absolute flex items-center justify-center rounded-full bg-white transition duration-200 ease-in-out shadow-sm pointer-events-none w-5 h-5"
        :class="[iconState ? 'translate-x-full' : 'translate-x-0']"
      >
        <component
          :is="iconState ? LockClosedIcon : LockOpenIcon"
          class="w-3 h-3"
          :class="[iconState ? 'text-red-600' : 'text-green-500']"
        />
      </div>
    </div>
  </button>
</template>

<script lang="ts" setup>
import { LockClosedIcon, LockOpenIcon } from '@heroicons/vue/20/solid'
import { HttpException } from '@core/exceptions/http.exception'
import useSnackbar from '@app/composables/use-snackbar'
import useLoading from '@app/composables/use-loading'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import { ReviewLockState } from '@core/domain/types/reviewLockState.type'
import { SnackbarState } from '@app/types'
import { computed } from 'vue'

const snackbar = useSnackbar()
const loading = useLoading()
const review = injectStrict(ReviewKey)

const iconState = computed(
  () => review.entity.value.plan?.lockState === ReviewLockState.LOCKED,
)

async function togglePlanLock() {
  loading.start()
  try {
    if (review.entity.value.plan?.lockState === ReviewLockState.LOCKED) {
      await unlockPlan()
    } else {
      await lockPlan()
    }
  } finally {
    loading.stop()
  }
}

async function lockPlan() {
  try {
    await review.lockPlan(review.entity.value.id)
  } catch (e) {
    const error = e as HttpException
    if (
      error?.response?.data?.statusCode >= 400 &&
      error?.response?.data?.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while locking review plan, please try again.',
      )
    }
    throw e
  }
}

async function unlockPlan() {
  try {
    await review.unlockPlan(review.entity.value.id)
  } catch (e) {
    const error = e as HttpException
    if (
      error?.response?.data?.statusCode >= 400 &&
      error?.response?.data?.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while unlocking review plan, please try again.',
      )
    }
    throw e
  }
}
</script>
